import {Component, Input, OnInit} from '@angular/core';
import {EventResponse, TrainingDayCapacityResponse} from '../../../../../../../../../../@shared/models/response.module';
import {TrainingService} from '../../../../../../../../../../@shared/services/training.service';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {AQH_TRN_1} from '../../../../../../../../../../@shared/models/input.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

interface PackageDayForm {
  training_date: string;
  dayLabel: string;
  session_count: number;
  hasCapacity: boolean;
}

@Component({
  selector: 'app-add-package',
  standalone: false,
  templateUrl: './add-package.html',
  styleUrl: './add-package.css'
})
export class AddPackage implements OnInit {
  @Input() event!: EventResponse;

  form: FormGroup;
  saving = false;
  isFree = false;

  sessionTypeOptions: { value: string; label: string }[] = [];

  vatRateOptions = [
    { value: 0, label: '0%' },
    { value: 12.5, label: '12.5%' },
    { value: 25, label: '25%' }
  ];

  currencyOptions = [
    { code: 'NOK', name: 'NOK' },
    { code: 'SEK', name: 'SEK' },
    { code: 'EUR', name: 'EUR' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'DKK', name: 'DKK' },
  ];

  // Day distribution
  dayCapacities: TrainingDayCapacityResponse[] = [];
  packageDays: PackageDayForm[] = [];

  constructor(
    private trainingService: TrainingService,
    private fb: FormBuilder,
    private modal: NgbActiveModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      package_name: ['', [Validators.required, Validators.maxLength(200)]],
      package_description: [''],
      session_count: [1, [Validators.required, Validators.min(1)]],
      session_type: ['group', Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      currency: ['NOK'],
      vat_rate: [0]
    });
  }

  ngOnInit() {
    this.sessionTypeOptions = [
      { value: 'group', label: this.translateService.instant('manageEvent.addPackage.typeGroup') },
      { value: 'individual', label: this.translateService.instant('manageEvent.addPackage.typeIndividual') },
      { value: 'both', label: this.translateService.instant('manageEvent.addPackage.typeBoth') }
    ];
    this.loadDayCapacities();
    this.initializeCurrency();
  }

  initializeCurrency() {
    // Set currency from event's default_currency (which was set during event creation)
    if (this.event?.default_currency) {
      this.form.get('currency')?.setValue(this.event.default_currency);
    }
  }

  loadDayCapacities() {
    if (!this.event) return;

    this.trainingService.getDayCapacity({ event_id: this.event.event_id }).subscribe({
      next: (capacities) => {
        this.dayCapacities = capacities;
        this.initializePackageDays();
      },
      error: () => {
        this.dayCapacities = [];
        this.initializePackageDays();
      }
    });
  }

  initializePackageDays() {
    if (!this.event?.start_date || !this.event?.end_date) return;

    const startDate = this.parseDate(this.event.start_date);
    const endDate = this.parseDate(this.event.end_date);

    if (!startDate || !endDate) return;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    this.packageDays = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const dayName = dayNames[currentDate.getDay()];
      const dayNum = currentDate.getDate();
      const monthName = currentDate.toLocaleString('default', { month: 'short' });

      const capacity = this.dayCapacities.find(c => {
        const capacityDateNormalized = this.normalizeDateToISO(c.training_date);
        return capacityDateNormalized === dateStr;
      });
      const hasCapacity = capacity ? (capacity.max_group_sessions > 0 || capacity.max_individual_sessions > 0) : false;

      this.packageDays.push({
        training_date: dateStr,
        dayLabel: `${dayName}, ${monthName} ${dayNum}`,
        session_count: 0,
        hasCapacity
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  normalizeDateToISO(dateValue: string | null | undefined): string | null {
    if (!dateValue) return null;
    const dateStr = String(dateValue);
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateStr.split('T')[0];
    }
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim();
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }
    return null;
  }

  parseDate(dateValue: string | Date | null | undefined): Date | null {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    }
    const dateStr = String(dateValue);
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim();
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = +parts[0];
        const day = +parts[1];
        const year = +parts[2];
        return new Date(year, month - 1, day);
      }
    }
    const datePart = dateStr.split('T')[0].split(' ')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return new Date(+parts[0], +parts[1] - 1, +parts[2]);
    }
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return null;
  }

  getTotalDaySessions(): number {
    return this.packageDays.reduce((sum, d) => sum + d.session_count, 0);
  }

  getDaysWithCapacity(): PackageDayForm[] {
    return this.packageDays.filter(d => d.hasCapacity);
  }

  v(name: string) {
    return this.form.get(name)?.value;
  }

  toggleFree(checked: boolean) {
    this.isFree = checked;
    if (checked) {
      this.form.get('price')?.setValue(0);
    }
  }

  save() {
    if (this.form.invalid || !this.event?.event_id) {
      this.form.markAllAsTouched();
      this.toastService.error(this.translateService.instant('manageEvent.addPackage.validationError'));
      return;
    }

    // Validate day distribution if days with capacity exist
    const daysWithCapacity = this.getDaysWithCapacity();
    if (daysWithCapacity.length > 0) {
      const totalAllocated = this.getTotalDaySessions();
      const sessionCount = this.v('session_count');
      if (totalAllocated !== sessionCount) {
        this.toastService.error(this.translateService.instant('manageEvent.addPackage.distributionMismatch'));
        return;
      }
    }

    this.saving = true;

    const payload: AQH_TRN_1 = {
      event_id: this.event.event_id,
      package_name: this.v('package_name'),
      package_description: this.v('package_description') || undefined,
      session_count: this.v('session_count'),
      session_type: this.v('session_type'),
      price_cents: Math.round(this.v('price') * 100),
      currency: this.v('currency'),
      vat_rate: this.v('vat_rate')
    };

    const packageDaysData = this.packageDays
      .filter(d => d.session_count > 0)
      .map(d => ({ training_date: d.training_date, session_count: d.session_count }));

    this.trainingService.createPackage(payload).subscribe({
      next: (createdPackage) => {
        // Save package days if any are configured
        if (packageDaysData.length > 0 && createdPackage) {
          this.trainingService.setPackageDays({
            package_id: createdPackage.package_id,
            package_days: packageDaysData
          }).subscribe({
            next: () => {
              this.saving = false;
              this.toastService.success(this.translateService.instant('manageEvent.addPackage.success'));
              this.modal.close(createdPackage);
            },
            error: () => {
              // Package was created but days failed - still close with success
              this.saving = false;
              this.toastService.success(this.translateService.instant('manageEvent.addPackage.success'));
              this.modal.close(createdPackage);
            }
          });
        } else {
          this.saving = false;
          this.toastService.success(this.translateService.instant('manageEvent.addPackage.success'));
          this.modal.close(createdPackage);
        }
      },
      error: (error) => {
        console.error('Error creating package:', error);
        this.saving = false;
        this.toastService.error(this.translateService.instant('manageEvent.addPackage.error'));
      }
    });
  }

  close() {
    this.modal.dismiss();
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00
   */
  calculateNetAmount(price: number): number {
    if (!price || price <= 0) return 0;
    const net = price * 0.956 - 2;
    return Math.max(0, Math.round(net * 100) / 100);
  }
}
